<script lang="ts" setup>
  import { computed, watch } from 'vue'
  import { DEFAULT_MAX_VALUE, DEFAULT_MIN_VALUE } from './BaseCounterInput.constants'
  import { TEST_ID } from './BaseCounterInput.constants'
  import CounterInput from './components/CounterInput/CounterInput.vue'
  import CounterButton from './components/CounterButton/CounterButton.vue'
  import MinusIcon from '@/shared/components/icons/MinusIcon.vue'
  import PlusIcon from '@/shared/components/icons/PlusIcon.vue'

  interface Props {
    modelValue: number
    maxValue?: number
    minValue?: number
    minDisabled?: boolean
    maxDisabled?: boolean
  }

  interface Emits {
    (e: 'update:modelValue', value: number): void
  }

  const props = withDefaults(defineProps<Props>(), {
    maxValue: DEFAULT_MAX_VALUE,
    minValue: DEFAULT_MIN_VALUE,
  })
  const emit = defineEmits<Emits>()
  const value = computed<number>({
    get() {
      return props.modelValue
    },
    set(newValue: number) {
      emit('update:modelValue', Number.isNaN(newValue) ? props.minValue : newValue)
    },
  })

  const decrease = () => {
    if (props.minDisabled) return

    if (value.value > props.minValue) {
      value.value = value.value - 1
    }
  }

  const increase = () => {
    if (props.maxDisabled) return

    if (value.value < props.maxValue) {
      value.value = value.value + 1
    }
  }

  // TODO: fix some problem with changing 2 digit value 10 => 11 by input
  const onChange = (newValue: number) => {
    value.value = newValue
  }

  const maxLength = computed((): number => {
    const maxValue = String(props.maxValue).length
    const minValue = String(props.minValue).length
    return minValue >= maxValue ? minValue : maxValue
  })

  watch(
    () => value.value,
    () => {
      if (value.value < props.minValue) value.value = props.minValue
      else if (value.value > props.maxValue) value.value = props.maxValue
    },
    { immediate: true }
  )
</script>

<template>
  <div class="counter" :data-testid="TEST_ID">
    <CounterButton :disabled="minDisabled" @click="decrease">
      <MinusIcon />
    </CounterButton>
    <CounterInput :model-value="value" :max-length="maxLength" @change="onChange" />
    <CounterButton :disabled="maxDisabled" @click="increase">
      <PlusIcon />
    </CounterButton>
  </div>
</template>

<style lang="scss" scoped>
  @import './BaseCounterInput';
</style>
